'use client'

/**
 * Purpose: Thin, one-function-per-event PostHog capture wrappers, grouped by
 * feature area, so callers never build event names/properties inline.
 * Inputs: feature-specific primitives per function (ids, booleans, counts).
 * Outputs: none returned; each function is a `capture()` side effect only.
 */

import { capture } from '@lib/posthog-capture'

// ─── Account ──────────────────────────────────────────────────────────────

export function captureAccountDeleted(): void {
  capture('account_deleted')
}

// ─── Products ─────────────────────────────────────────────────────────────

export function captureBuyNowClicked(productId: string): void {
  capture('buy_now_clicked', { product_id: productId })
}

/** Fired when a category chip on /products jumps the viewport to that section. */
export function captureProductCategoryJump(category: string): void {
  capture('product_category_jumped', { category })
}

/**
 * Fired when a compatibility rating pill in the /products sticky header is
 * toggled. `score` is the raw 0–4 bucket; `selected` is the state it moved to.
 */
export function captureCompatibilityFilterToggled(score: number, selected: boolean): void {
  capture('compatibility_filter_toggled', { score, percent: score * 25, selected })
}

/** Fired when a /products category strip is advanced via its scroll arrow. */
export function captureProductStripScrolled(category: string, direction: 'left' | 'right'): void {
  capture('product_strip_scrolled', { category, direction })
}

/** Fired when the /products filter panel is opened from the sticky header. */
export function captureProductFiltersOpened(): void {
  capture('product_filters_opened')
}

/** Shared by every routine add/remove toggle, wherever it's rendered. */
export function captureRoutineToggle(action: 'added' | 'removed', productId: string): void {
  const eventName = action === 'added' ? 'product_added_to_routine' : 'product_removed_from_routine'
  capture(eventName, { product_id: productId })
}

// ─── Scan ─────────────────────────────────────────────────────────────────

export function captureProductScanned(method: string, resultsCount: number): void {
  capture('product_scanned', {
    method,
    results_count: resultsCount,
    found: resultsCount > 0,
  })
}

// PRD — Scanner §8 client events. Server-side events (match, OCR, non-skincare) are in lib/scan/telemetry.ts.

export function captureScanStarted(method: 'barcode' | 'label_photo', entryPoint: string): void {
  capture('scan_started', { method, entry_point: entryPoint })
}

// Mobile fullscreen scanner: the user closes it to use search, or reopens it from the page.
export function captureScannerToggled(action: 'closed' | 'opened'): void {
  capture('scanner_toggled', { action })
}

export function captureBarcodeDecoded(barcodeValue: string, barcodeFormat: 'ean13' | 'upc12' | 'other'): void {
  capture('barcode_decoded', { barcode_value: barcodeValue, barcode_format: barcodeFormat })
}

export function captureCompatibilityVerdictShown(props: {
  productId: string | null
  compatibilityResult: string
}): void {
  capture('compatibility_verdict_shown', {
    ...(props.productId ? { product_id: props.productId } : {}),
    compatibility_result: props.compatibilityResult,
  })
}

// ─── Auth ─────────────────────────────────────────────────────────────────

// No beta gate: every credentialed signup completes immediately, so there is
// no pending-approval/beta-granted distinction left to report.
export function captureUserSignedUp(): void {
  capture('user_signed_up', { method: 'email' })
}

export function captureUserLoggedIn(loginType: 'first' | 'return'): void {
  capture('user_logged_in', { method: 'email', login_type: loginType })
}

// ─── Scan ─────────────────────────────────────────────────────────────────

/** Fired when the user leaves the post-quiz results screen on the scan page. */
export function captureScanSurveyResultsContinued(hasPendingScan: boolean): void {
  capture('scan_survey_results_continued', { has_pending_scan: hasPendingScan })
}

// ─── Contact ──────────────────────────────────────────────────────────────

export function captureContactFormSubmitted(): void {
  capture('contact_form_submitted')
}

// ─── Survey ───────────────────────────────────────────────────────────────

export function captureSurveyStarted(fromOnboarding: boolean): void {
  capture('survey_started', { from_onboarding: fromOnboarding })
}

export function captureSurveySubmitFailed(
  reason: 'error' | 'validation',
  fromOnboarding: boolean,
  fields?: string[],
): void {
  capture('survey_submit_failed', {
    reason,
    from_onboarding: fromOnboarding,
    ...(fields ? { fields } : {}),
  })
}

export function captureSurveyCompleted(props: {
  fromOnboarding: boolean
  age?: string
  gender?: string
}): void {
  capture('survey_completed', {
    from_onboarding: props.fromOnboarding,
    age: props.age || undefined,
    gender: props.gender || undefined,
  })
}

// ─── Diary ────────────────────────────────────────────────────────────────

export function captureDiaryEntryAutogenerated(timeOfDay: string, rating: number): void {
  capture('diary_entry_autogenerated', { time_of_day: timeOfDay, rating })
}

/** Fired when the no-consent auto-generate info line's inline link opens the enable-AI modal. */
export function captureDiaryAiConsentPromptClicked(): void {
  capture('diary_ai_consent_prompt_clicked')
}

/** Fired on "Not now" — the enable-AI-image-processing modal closed without enabling. */
export function captureDiaryAiConsentModalDismissed(): void {
  capture('diary_ai_consent_modal_dismissed')
}

/** Fired when AI-image-processing consent is turned on from the diary auto-generate modal. */
export function captureDiaryAiConsentEnabled(): void {
  capture('diary_ai_consent_enabled')
}

/** Shared by both the create and update paths of the diary log form. */
export function captureDiaryEntrySaved(
  mode: 'created' | 'updated',
  props: {
    timeOfDay: string
    rating: number
    moodRating?: number
    productCount: number
    hasFaceImage: boolean
  },
): void {
  const eventName = mode === 'created' ? 'diary_entry_created' : 'diary_entry_updated'
  capture(eventName, {
    time_of_day: props.timeOfDay,
    rating: props.rating,
    mood_rating: props.moodRating || undefined,
    product_count: props.productCount,
    has_face_image: props.hasFaceImage,
  })
}

// ─── Admin catalog ────────────────────────────────────────────────────────

/** Fired when the Products / Ingredients tab pill changes. */
export function captureAdminCatalogTabChanged(tab: 'products' | 'ingredients'): void {
  capture('admin_catalog_tab_changed', { tab })
}

/**
 * Fired once per debounced search URL update, not per keystroke.
 * Query text stays out of the payload — length only, so a typed name is not stored.
 */
export function captureAdminCatalogSearchPerformed(
  tab: 'products' | 'ingredients',
  queryLength: number,
): void {
  capture('admin_catalog_search_performed', { tab, query_length: queryLength })
}

export function captureAdminCatalogCategorySelected(category: string): void {
  capture('admin_catalog_category_selected', { category })
}

export function captureAdminCatalogFlaggedOnlyToggled(selected: boolean): void {
  capture('admin_catalog_flagged_only_toggled', { selected })
}

export function captureAdminProductIngredientTokenAdded(productId: string, created: boolean): void {
  capture('admin_product_ingredient_token_added', { product_id: productId, created })
}

export function captureAdminProductIngredientTokenRemoved(productId: string): void {
  capture('admin_product_ingredient_token_removed', { product_id: productId })
}

export function captureAdminProductSkinMatchToggled(
  productId: string,
  trait: string,
  value: string,
  selected: boolean,
): void {
  capture('admin_product_skin_match_toggled', { product_id: productId, trait, value, selected })
}

export function captureAdminProductFlaggedToggled(productId: string, flagged: boolean): void {
  capture('admin_product_flagged_toggled', { product_id: productId, flagged })
}

export function captureAdminIngredientDrawerOpened(mode: 'add' | 'edit'): void {
  capture('admin_ingredient_drawer_opened', { mode })
}

/**
 * Same event name as `captureIngredientDisplaySaved` in `lib/ingredients/telemetry.ts`.
 * The drawer is a client component, so it cannot call the server helper (that one
 * needs a distinct id and the server PostHog client).
 */
export function captureAdminIngredientDisplaySaved(ingredientId: string | null, mode: 'add' | 'edit'): void {
  capture('admin_ingredient_display_saved', {
    ingredient_id: ingredientId,
    mode,
    source: 'ingredient_drawer',
  })
}

/** Fired when an admin opens the merge dialog from the Ingredients tab with rows selected. */
export function captureAdminIngredientMergeOpened(selectedCount: number): void {
  capture('admin_ingredient_merge_opened', { selected_count: selectedCount })
}

/** Fired when an admin confirms a merge; the server records the outcome as `admin_ingredients_merged`. */
export function captureAdminIngredientMergeConfirmed(sourceCount: number): void {
  capture('admin_ingredient_merge_confirmed', { source_count: sourceCount })
}

/** Fired when an admin starts an Enrich run on the Ingredients tab, or continues a stopped one. */
export function captureAdminCatalogEnrichStarted(ingredientCount: number, continued: boolean): void {
  capture('admin_catalog_enrich_started', { ingredient_count: ingredientCount, continued })
}

/** Fired when an admin presses Stop during an Enrich run; counts are ingredients, not lookups. */
export function captureAdminCatalogEnrichStopped(completed: number, total: number): void {
  capture('admin_catalog_enrich_stopped', { completed, total })
}

export function captureAdminInsightsHeatmapCellOpened(category: string, skinCode: string): void {
  capture('admin_insights_heatmap_cell_opened', { category, skin_code: skinCode })
}

// ─── Admin categories (admin-product-categories) ──────────────────────────

/** Fired from CategoriesManager on every create/update/move/delete attempt, success or not —
 * `reason` carries the server's error code (e.g. `label_taken`, `in_use`) on failure. */
export function captureAdminCategoryCreated(slug: string | null, reason?: string): void {
  capture('admin_category_created', { slug, ...(reason ? { reason } : {}) })
}

export function captureAdminCategoryUpdated(slug: string, reason?: string): void {
  capture('admin_category_updated', { slug, ...(reason ? { reason } : {}) })
}

export function captureAdminCategoryMoved(slug: string, direction: 'up' | 'down', reason?: string): void {
  capture('admin_category_moved', { slug, direction, ...(reason ? { reason } : {}) })
}

export function captureAdminCategoryDeleted(slug: string): void {
  capture('admin_category_deleted', { slug })
}

/** Fired instead of `captureAdminCategoryDeleted` when the server refuses the delete
 * (in-use count, or the protected `other` fallback). */
export function captureAdminCategoryDeleteBlocked(slug: string, reason: string): void {
  capture('admin_category_delete_blocked', { slug, reason })
}

// ─── Ingredient safety (Phase 2) ──────────────────────────────────────────

/**
 * Fired when IngredientSafetyText mounts on a surface. No PII — ids and
 * status enum only. `scan_result` wiring lands with PRD — Scanner.
 */
export function captureIngredientSafetyTextShown(props: {
  ingredientId?: string
  productId?: string
  safetyStatus: string | null
  surface: 'product_page' | 'admin_ingredient' | 'scan_result'
}): void {
  capture('ingredient_safety_text_shown', {
    ingredient_id: props.ingredientId,
    product_id: props.productId,
    safety_status: props.safetyStatus ?? 'not_yet_assessed',
    surface: props.surface,
  })
}
